import env from '../../env';
const axios = require('axios');
import { constants } from '../../utils/constants';
const paperworkLogo = require('@/assets/images/logo.svg');
const paperworkSquareLogo = require('@/assets/images/default_space_logo.png');
const paperworkSquareLogoInvert = require('@/assets/images/invert_space_logo.png');
import api from '../../api';
import * as Sentry from '@sentry/browser';

export default {
    namespaced: true,
    state: {
        loading: true,
        fullScreenLoading: true,
        envLoaded: false,
        loadingMessage: '',
        domain: window.location.host,
        subdomain: '',
        protocol: window.location.protocol,
        fromRoute: null,
        developerError: null,
        isAuthRoute: false,
        apiVersion: '',
        isDev: process.env.NODE_ENV === 'development' ||
            process.env.VUE_APP_CONTEXT === 'qa' ||
            process.env.VUE_APP_CONTEXT === 'preview',
        appLoadedAt: Date.now(),
        appStatusLastUpdated: 0,
        appStatus: constants.DEFAULT_APP_STATUS,
        isEmbedded: false,
        partner: null,
        partnerLoaded: false,
        currentRoute: {
            path: '/',
            name: null,
            hash: '',
            query: {},
            params: {},
            fullPath: '/',
            matched: [],
            meta: {},
        },
    },
    getters: {
        loading(state) {
            return state.loading;
        },
        fullScreenLoading(state) {
            return state.fullScreenLoading;
        },
        fromRoute(state) {
            return state.fromRoute;
        },
        envLoaded(state) {
            return state.envLoaded;
        },
        loadingMessage(state) {
            return state.loadingMessage;
        },
        url(state, getters) {
            if (getters.appBranding && getters.appBranding.domain) {
                return `${state.protocol}//${getters.appBranding.domain}`;
            }
            let domain = state.domain;
            if (state.subdomain && state.subdomain != "none") {
                domain = `${state.subdomain}.${domain}`;
            }
            return `${state.protocol}//${domain}`;
        },
        domain(state, getters) {
            if (getters.appBranding && getters.appBranding.domain) {
                return getters.appBranding.domain;
            }
            return state.domain;
        },
        developerError(state) {
            return state.developerError;
        },
        isAuthRoute(state) {
            return state.isAuthRoute;
        },
        apiVersion(state) {
            return state.apiVersion;
        },
        isDev(state) {
            return state.isDev;
        },
        appLoadedAt(state) {
            return state.appLoadedAt;
        },
        appStatusLastUpdated(state) {
            return state.appStatusLastUpdated;
        },
        appStatus(state) {
            return state.appStatus;
        },
        isEmbedded(state) {
            return state.isEmbedded;
        },
        appBranding(state, getters, rootState, rootGetters) {
            return rootGetters['org/org'].app_branding ||
                getters.partner?.branding_settings ||
                false;
        },
        appName(state, getters, rootState, rootGetters) {
            // Wait for the org/partner to load so the default name doesn't flicker
            if (!rootGetters['org/exists'] && !state.partnerLoaded) return '';
            if (!getters.appBranding || !getters.appBranding.name) return 'Paperwork';
            return getters.appBranding.name;
        },
        logoUrlUnsafe(state, getters) {
            if (!getters.appBranding || !getters.appBranding.logo) return paperworkLogo;
            return getters.appBranding.logo;
        },
        logoUrl(state, getters, rootState, rootGetters) {
            // Wait for the org/partner to load so the default logo doesn't flicker
            if (!rootGetters['org/exists'] && !state.partnerLoaded) return '';
            return getters.logoUrlUnsafe;
        },
        logoSquareUrl(state, getters, rootState, rootGetters) {
            // Wait for the org/partner to load so the default logo doesn't flicker
            if (!rootGetters['org/exists'] && !state.partnerLoaded) return '';
            if (!getters.appBranding || !getters.appBranding.logoSquare) {
                return state.isEmbedded ? paperworkSquareLogoInvert : paperworkSquareLogo;
            }
            return getters.appBranding.logoSquare;
        },
        supportEmail(state, getters) {
            if (!getters.appBranding || !getters.appBranding.supportEmail) return 'admin@paperworkforms.com';
            return getters.appBranding.supportEmail;
        },
        salesEmail(state, getters) {
            if (!getters.appBranding || !getters.appBranding.salesEmail) return 'sales@paperworkforms.com';
            return getters.appBranding.salesEmail;
        },
        marketingSite(state, getters) {
            if (!getters.appBranding || !getters.appBranding.marketingSite) return 'https://paperworkforms.com';
            return getters.appBranding.marketingSite.replace(/\/$/, '');
        },
        marketingSiteDisplay(state, getters) {
            return getters.marketingSite.replace(/^https?:\/\//, '');
        },
        partner(state) {
            return state.partner;
        },
        currentRoute(state) {
            return state.currentRoute;
        },
    },
    mutations: {
        setFromRoute(state, fromRoute) {
            state.fromRoute = fromRoute;
        },
        setLoading(state, { isLoading, fullScreen = true }) {
            state.loading = isLoading;
            state.fullScreenLoading = fullScreen;
            if (!isLoading) {
                state.loadingMessage = '';
                state.fullScreenLoading = true;
            }
        },
        loadingMessage(state, message) {
            state.loadingMessage = message;
        },
        envLoaded(state, envLoaded) {
            state.envLoaded = envLoaded;
        },
        domain(state, domain) {
            state.domain = domain;
        },
        subdomain(state, subdomain) {
            state.subdomain = subdomain;
        },
        developerError(state, msg) {
            state.developerError = msg;
        },
        isAuthRoute(state, isAuthRoute) {
            state.isAuthRoute = isAuthRoute;
        },
        apiVersion(state, version) {
            state.apiVersion = version;
        },
        isDev(state, isDev) {
            state.isDev = isDev;
        },
        setAppStatusLastUpdated(state, timestamp) {
            state.appStatusLastUpdated = timestamp;
        },
        setAppStatus(state, appStatus) {
            for (let key in state.appStatus) {
                if (appStatus[key] !== undefined) state.appStatus[key] = appStatus[key];
            }
        },
        isEmbedded(state, isEmbedded) {
            state.isEmbedded = isEmbedded;
        },
        partner(state, partner) {
            state.partner = partner;
        },
        partnerLoaded(state, partnerLoaded) {
            state.partnerLoaded = partnerLoaded;
        },
        currentRoute(state, route) {
            state.currentRoute = route;
        },
    },
    actions: {
        async updateAppStatus({ getters, commit }, force = false) {
            if (!force && Date.now() - getters.appStatusLastUpdated < 5 * 60 * 1000) return;

            let url = parseInt(localStorage.useQAStatusEndpoint) ? 'https://maestro-paperwork.s3.us-east-2.amazonaws.com/status-qa.json' : env.APP_STATUS_URL;

            if (url) {
                let res = await axios.get(url)
                commit('setAppStatus', res.data);
            }

            commit('setAppStatusLastUpdated', Date.now());
        },
        async loadPartnerBranding({ state, commit }) {
            if (state.partnerLoaded) return;
            const domain = window.location.host;
            if (domain.includes(state.domain)) {
                commit('partnerLoaded', true);
                return;
            }
            try {
                const partner = await api.get('d/partner', { domain });
                commit('partner', partner);
                commit('partnerLoaded', true);
            } catch (error) {
                if (error.httpCode === 404) {
                    console.warn(error.message);
                    Sentry.captureMessage(`Partner not found for custom domain "${domain}"`, 'warning');
                } else {
                    console.error(error);
                    Sentry.captureMessage(error.message, 'error');
                }
            }
        }
    },
}